import React, { useMemo, useState, useEffect, useContext } from 'react';
import { getDeviceOperationalState, getScreenNowPlaying, hdmiPort, foregroundActivity } from '../../utils/screenActivity';
import { nativeStandbyPermission, nativeStartupPermission, nativePowerPermissionLabel, useDeviceAdminResponses } from './nativeStandbyPermission';
import { getEquipment, isVideoConnection } from '../../utils/equipment';
import { buildInputInventory, inputCandidates } from './hdmiInputInventory';
import { hasSourceActivationRoute } from './sourceActivation';
import { adbConnectionSignal, adbConnectionDetail } from './useAreaConnections';
import { channelSnapshotFresh } from '../../utils/channelFreshness';
import DevicePowerDetails, { DevicePowerBadge, AreaPowerContext } from './DevicePowerDetails';
import { resolvePowerObservations } from '../../utils/powerObservations';
import ScreenPreview from './ScreenPreview';
import { resolveScreenshotTarget, showsSuitestream, canShowActivity } from './screenPreviewTarget';
import useScreenPreviews from './useScreenPreviews';
import WirelessDebuggingReconnect from './WirelessDebuggingReconnect';
import WirelessDebuggingRecoveryStatus, { useRecoveryFacts } from '../SiteControl/WirelessDebuggingRecoveryStatus';

export { getDeviceClockStatus, getDeviceOperationalState, getScreenNowPlaying, hdmiPort } from '../../utils/screenActivity';

const remoteTypes = new Set([
  'staff', 'phone remote', 'staff phone', 'phone_remote', 'staff_phone',
  'control console', 'console remote', 'control_console', 'console_remote',
]);

export function isRemoteDevice(device) {
  return remoteTypes.has(String(device?.type || '').trim().toLowerCase());
}

export function getDeviceAvailability(device, liveState) {
  if (!channelSnapshotFresh(liveState)) return 'unknown';
  if (liveState?.available === true) return 'online';
  if (liveState?.available === false) return 'offline';
  return 'unknown';
}


function parseObject(value) {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function getDeviceAccessibilityState(device, liveState) {
  const liveValue = liveState?.state?.externalApp?.accessibilityEnabled
    ?? liveState?.state?.accessibilityEnabled;
  if (typeof liveValue === 'boolean') return liveValue ? 'enabled' : 'disabled';
  const integration = parseObject(device?.integration_data || device?.integrationData);
  const storedValue = parseObject(integration.appNode).accessibilityEnabled;
  if (typeof storedValue === 'boolean') return storedValue ? 'enabled' : 'disabled';
  return 'unknown';
}

export function getDeviceSystemOverlayState(device, liveState) {
  const liveValue = liveState?.state?.externalApp?.systemOverlayEnabled
    ?? liveState?.state?.systemOverlayEnabled
    ?? liveState?.state?.externalApp?.overlayEnabled
    ?? liveState?.state?.overlayEnabled;
  if (typeof liveValue === 'boolean') return liveValue ? 'enabled' : 'disabled';
  const integration = parseObject(device?.integration_data || device?.integrationData);
  const appNode = parseObject(integration.appNode);
  const storedValue = appNode.systemOverlayEnabled ?? appNode.overlayEnabled;
  if (typeof storedValue === 'boolean') return storedValue ? 'enabled' : 'disabled';
  return 'unknown';
}

export function getDeviceCapabilitySignals(device, liveState) {
  const accessibility = getDeviceAccessibilityState(device, liveState);
  const overlays = getDeviceSystemOverlayState(device, liveState);
  return {
    accessibility: {
      state: accessibility,
      label: accessibility === 'enabled'
        ? 'Accessibility On'
        : accessibility === 'disabled'
          ? 'Accessibility Off'
          : 'Accessibility Unknown',
    },
    overlays: {
      state: overlays,
      label: overlays === 'enabled'
        ? 'Overlays Enabled'
        : overlays === 'disabled'
          ? 'Overlays Disabled'
          : 'Overlays Unknown',
    },
  };
}

export function evaluatePlayerSourceActivation(expectedInput, report) {
  const expectedPort = hdmiPort(expectedInput);
  const activeSource = report?.activeSource;
  const reportedPort = activeSource?.available ? Number(activeSource.port) || null : null;
  return {
    expectedPort,
    reportedPort,
    confirmed: Boolean(expectedPort && reportedPort === expectedPort),
    evidenceSource: activeSource?.adapter || report?.source || null,
  };
}

function enabledInputPorts(screen) {
  return new Set((screen?.activationStrategy?.automationProfiles || [])
    .filter((profile) => profile?.goal === 'input_selector' && profile.trainingState === 'validated')
    .map((profile) => Number(profile?.finalVerification?.observations
      ?.find((observation) => observation.type === 'tv_active_input')?.expectedPort))
    .filter(Number.isInteger));
}

function hasEnabledTvHome(screen) {
  if (screen.nativeConnector === 'lg_webos_remote' || screen.inputReport?.connector === 'lg_webos_remote') return true;
  return (screen?.activationStrategy?.automationProfiles || []).some((profile) => (
    profile?.goal === 'input_selector'
    && profile.trainingState === 'validated'
    && profile.finalVerification?.observations?.some((observation) => observation.type === 'tv_home')
  ));
}

export function buildHdmiMappingSuggestions(topology, devices, tvInputReports = {}) {
  return (topology?.screens || []).flatMap((screen) => {
    if (screen.displayKind === 'unmanaged' || !screen.televisionDeviceId) return [];
    const report = screen.inputReport || tvInputReports[Number(screen.televisionDeviceId)];
    return buildInputInventory(screen, report).flatMap((group) => inputCandidates(group, devices || []).flatMap(({ device, child, confidence }) => {
      const player = (screen.players || []).find((entry) => Number(entry.deviceId || entry.id) === Number(device.id));
      if (!player || !isVideoConnection(player, device) || !hdmiPort(group.input)
        || (player.mappingMode === 'confirmed' && hdmiPort(player.expectedInput) === hdmiPort(group.input))) return [];
      return [{
        screenId: screen.id,
        playerDeviceId: Number(device.id),
        playerName: device.name,
        expectedInput: group.input,
        ...(report?.connector === 'lg_webos_remote' && group.physical?.nativeInputId ? { connectionConfig: {
          ...player.connectionConfig, inputRoute: { schemaVersion: 1, connector: report.connector,
            nativeInputId: group.physical.nativeInputId, canonicalPort: group.physical.canonicalPort,
            identityReference: report.identityReference, validation: { status: 'not_tested' } },
        } } : {}),
        mappingMode: 'suggested',
        mappingConfidence: confidence,
        mappingEvidence: {
          source: report?.source || 'android.media.tv.TvInputManager',
          inputId: child.id,
          parentInputId: child.parentId || null,
          reportedLabel: child.label,
        },
      }];
    }));
  });
}

function StatusBadge({ state, label = state }) {
  return <span className={`topology-live__status topology-live__status--${state}`}>{label}</span>;
}

function reportedApp(liveState) {
  const activity = foregroundActivity(liveState);
  return activity.fresh ? activity.appName || activity.packageName || null : null;
}

export function wirelessDebuggingNeedsAttention(connection) {
  return connection?.authMode === 'tls' && connection?.checkWirelessDebugging === true;
}

function WirelessDebuggingWarning({ connection }) {
  const recovery = useRecoveryFacts(connection?.wirelessDebuggingRecovery);
  const nextCheck = connection?.nextCheckAtEpochMs;
  return <span className="topology-live__accessibility-warning">
    <strong>Check Wireless Debugging</strong>
    {recovery.fresh && recovery.debugging === 'Enabled'
      ? 'This paired ADB connection cannot be reached, although the target reports Wireless Debugging enabled. Check device power, network and Wi-Fi approval.'
      : recovery.fresh && recovery.debugging === 'Disabled'
        ? 'The target reports Wireless Debugging disabled. Check its recovery status and any Wi-Fi approval prompt.'
        : 'This paired device cannot be reached. Wireless Debugging may have turned off after a restart. Check Android Settings → Developer options → Wireless debugging first and turn it on if needed.'}
    Suitestream will reconnect automatically. If it is already on, check the device’s power and network connection.
    <small>{connection?.recovering ? 'Reconnecting now…' : Number.isFinite(nextCheck)
      ? `Next automatic check: ${new Date(nextCheck).toLocaleTimeString()}.` : 'Automatic retries are continuing.'}
      {' '}For an immediate check, use Connect / Retry ADB in Site Control.</small>
  </span>;
}

function DeviceNode({ siteId, device, liveState, remotePaired, adbConnection, role, actions, onSelect, onOpenAndroidPowerSettings, onOpenDevicePairing }) {
  useDeviceAdminResponses();
  const standbyPermission = nativeStandbyPermission(device, liveState);
  const startupPermission = nativeStartupPermission(device, liveState);
  const availability = getDeviceAvailability(device, liveState);
  if (!device) {
    return (
      <div className="topology-live__device topology-live__device--missing">
        <span className="topology-live__device-role">{role}</span>
        <strong>Not assigned</strong>
        <StatusBadge state="unknown" />
      </div>
    );
  }
  const app = reportedApp(liveState);
  const adbSignal = adbConnectionSignal(adbConnection);
  const wirelessWarning = wirelessDebuggingNeedsAttention(adbConnection);
  if (getEquipment(device)?.management === 'unmanaged') {
    return <div className={`topology-live__device ${wirelessWarning ? 'topology-live__device--accessibility-warning' : ''}`}>
      <span className="topology-live__device-role">{role}</span>
      <strong>{device.name}</strong>
      {actions && <div className="topology-live__device-actions">{actions}</div>}
      <span className="topology-live__device-meta">Equipment · no direct status</span>
      <button type="button" className={`topology-live__signal topology-live__signal--adb ${adbSignal.className}`}
        disabled={!onOpenDevicePairing} title="Open Site Control / Pairing for this device"
        onClick={() => onOpenDevicePairing?.(device.id)}>{adbSignal.label}</button>
      {adbConnection?.authMode === 'tls' && adbConnection.wirelessDebuggingRecovery !== undefined && <WirelessDebuggingRecoveryStatus compact observation={adbConnection.wirelessDebuggingRecovery} />}
        {adbConnection?.authMode === 'tls' && ['disconnected', 'attention'].includes(adbConnection?.status || adbConnection?.state) && <WirelessDebuggingReconnect deviceId={device.id} siteId={siteId || device.site_id || device.siteId} />}
      {['Native standby', 'Native startup'].map((label) => <button key={label} type="button" className="topology-live__signal is-muted"
        disabled={!onOpenAndroidPowerSettings} onClick={() => onOpenAndroidPowerSettings?.(device.id)}>{label}: Unsupported</button>)}
      {wirelessWarning && <WirelessDebuggingWarning connection={adbConnection} />}
    </div>;
  }
  const operational = getDeviceOperationalState(liveState);
  const capabilitySignals = getDeviceCapabilitySignals(device, liveState);
  const accessibilityState = capabilitySignals.accessibility.state;
  const systemOverlayState = capabilitySignals.overlays.state;
  const settingsWarning = accessibilityState === 'disabled' || systemOverlayState === 'disabled' || wirelessWarning;
  return (
    <div className={`topology-live__device ${settingsWarning ? 'topology-live__device--accessibility-warning' : ''}`}>
      <button type="button" className="topology-live__device-select" onClick={() => onSelect?.(device.id)}>
        <span className="topology-live__device-role">{role}</span>
        <strong>{device.name || `Device ${device.id}`}</strong>
        <StatusBadge state={availability} label={availability === 'online' ? 'Reachable' : availability === 'offline' ? 'Unreachable' : 'Unknown'} />
      </button>
      {actions && <div className="topology-live__device-actions">{actions}</div>}
      <div className="topology-live__signals">
        <button type="button" className={`topology-live__signal topology-live__signal--adb ${adbSignal.className}`}
          disabled={!onOpenDevicePairing} title="Open Site Control / Pairing for this device"
          onClick={() => onOpenDevicePairing?.(device.id)}>{adbSignal.label}</button>
        {adbConnection?.authMode === 'tls' && adbConnection.wirelessDebuggingRecovery !== undefined && <WirelessDebuggingRecoveryStatus compact observation={adbConnection.wirelessDebuggingRecovery} />}
        {adbConnection?.authMode === 'tls' && ['disconnected', 'attention'].includes(adbConnection?.status || adbConnection?.state) && <WirelessDebuggingReconnect deviceId={device.id} siteId={siteId || device.site_id || device.siteId} />}
        {[['Native standby', standbyPermission], ['Native startup', startupPermission]].map(([label, state]) => (
          <button key={label} type="button" className={`topology-live__signal ${state === 'enabled' ? 'is-good' : 'is-muted'}`}
            disabled={!onOpenAndroidPowerSettings} title="Open Android Power Policy Diagnostics for this device"
            onClick={() => onOpenAndroidPowerSettings?.(device.id)}>
            {label}: {nativePowerPermissionLabel(state)}
          </button>
        ))}
        <DevicePowerBadge deviceId={device.id} liveState={liveState} fallback={operational.power} />
        <span className={`topology-live__signal ${operational.native.connected ? 'is-good' : 'is-muted'}`}
          title="Suitestream service connection for background device control. Player connectivity is shown separately.">{operational.native.label}</span>
        <span className={`topology-live__signal ${operational.player.connected ? 'is-good' : 'is-muted'}`}
          title="The playback page has its own connection and can remain connected while the background service reconnects.">{operational.player.label}</span>
        <span className={`topology-live__signal ${operational.monitoring.connected ? 'is-good' : 'is-muted'}`}>{operational.monitoring.label}</span>
        <span className="topology-live__signal" title="Last version reported by the background service; retained while disconnected.">
          Ver {liveState?.state?.channels?.native?.appVersion || 'Unknown'}{!operational.native.connected && liveState?.state?.channels?.native?.appVersion ? ' · last reported' : ''}
        </span>
        {adbConnection?.checkedAtEpochMs && <span className="topology-live__signal">{adbConnectionDetail(adbConnection)}</span>}
        {operational.player.connected && !operational.native.connected && <span className="topology-live__signal">
          Playback connected; background device control unavailable
        </span>}
        <span className={`topology-live__signal ${remotePaired === true ? 'is-good' : 'is-muted'}`}>Google Android TV Remote: {remotePaired === true ? 'Paired' : remotePaired === false ? 'Not paired' : 'Unknown'}</span>
        {!isRemoteDevice(device) && (
          <>
            <span className={`topology-live__signal topology-live__signal--capability-${capabilitySignals.accessibility.state}`}>
              {capabilitySignals.accessibility.label}
            </span>
            <span className={`topology-live__signal topology-live__signal--capability-${capabilitySignals.overlays.state}`}>
              {capabilitySignals.overlays.label}
            </span>
          </>
        )}
      </div>
      <div className="topology-live__device-details">
        {app && <dl className="topology-live__foreground"><dt>Foreground</dt><dd>{app}</dd></dl>}
        <DevicePowerDetails device={device} liveState={liveState} />
        <div className="topology-live__activity-notes">
          <span className={`topology-live__observation ${operational.clock.state === 'warning' ? 'topology-live__observation--warning' : ''}`}>{operational.clock.label}</span>
          {!operational.telemetryFresh && <span className="topology-live__observation topology-live__observation--warning">App activity unavailable or stale</span>}
        </div>
      </div>
      {wirelessWarning && <WirelessDebuggingWarning connection={adbConnection} />}
      {accessibilityState === 'disabled' && (
        <span className="topology-live__accessibility-warning">
          <strong>Accessibility is off</strong>
          Suitestream controls may fail. Re-enable Suitestream in Android Accessibility settings on this device.
        </span>
      )}
      {systemOverlayState === 'disabled' && (
        <span className="topology-live__accessibility-warning">
          <strong>Display over other apps is off</strong>
          Suitestream overlays may fail. Re-enable Display over other apps for Suitestream in Android settings on this device.
        </span>
      )}
    </div>
  );
}

function ScreenCard({ siteId, screen, devicesById, liveStates, remotePairing, adbConnections, tvInputReport, suggestions, sourceActivation, onActivatePlayer, onActivateTelevision, onLaunchSuitestream, onOpenDeviceRemote, onOpenAndroidPowerSettings, onOpenDevicePairing, onConfirmMapping, onDeviceSelect, entry, preview }) {
  const powerContext = useContext(AreaPowerContext);
  const television = devicesById.get(Number(screen.televisionDeviceId));
  const { nowPlaying, target } = entry;
  const activePort = nowPlaying.activePort;
  const activeDeviceId = nowPlaying.confirmed && activePort ? Number(nowPlaying.activeDeviceId) : null;
  const players = [...(screen.players || [])].sort((a, b) => (
    Number(Number(b.deviceId || b.id) === activeDeviceId) - Number(Number(a.deviceId || a.id) === activeDeviceId)
  ));
  const screenOff = nowPlaying.screenOff === true;
  const screenUnavailable = nowPlaying.screenUnavailable === true;
  const screenInactive = screenOff || screenUnavailable;
  const previewLabel = screenOff ? 'Screen off' : screenUnavailable ? 'Screen unavailable' : 'Now Playing';
  const switchingMethods = screen.activationStrategy?.inputSwitching?.methods || [];
  const enabledPorts = enabledInputPorts(screen);
  const tvHomeEnabled = hasEnabledTvHome(screen);
  const activationBusy = ['sending', 'verifying'].includes(sourceActivation?.status);
  return (
    <article className={`topology-live__screen ${screenInactive ? 'topology-live__screen--off' : ''}`}>
      <header className="topology-live__card-header">
        <div>
          <span className="topology-live__eyebrow">Logical Screen</span>
          <h3>{screen.name}</h3>
        </div>
        <div className="topology-live__card-header-actions">
          <span className="topology-live__kind">{screen.displayKind === 'unmanaged' ? 'Unmanaged display' : 'Managed TV'}</span>
        </div>
      </header>

      <div className={`topology-live__preview ${screenInactive ? 'topology-live__preview--off' : nowPlaying.confirmed ? 'topology-live__preview--confirmed' : ''}`} aria-label={`${previewLabel} on ${screen.name}`}>
        <div className="topology-live__preview-copy">
          <span className="topology-live__preview-label"><span className="topology-live__preview-dot" /> {previewLabel}</span>
          <strong className="topology-live__preview-title">{nowPlaying.title}</strong>
          <span className="topology-live__preview-source">{nowPlaying.source}</span>
        </div>
        <ScreenPreview key={target.key} nowPlaying={nowPlaying} target={target} preview={preview} />
      </div>

      <div className={`topology-live__tv-zone ${activeDeviceId ? 'topology-live__tv-zone--active' : ''}`}>
        {screen.displayKind === 'unmanaged' ? (
          <div className="topology-live__device topology-live__device--unmanaged">
            <span className="topology-live__device-role">Display</span>
            <strong>{screen.name}</strong>
            <span className="topology-live__device-meta">No direct control or status</span>
            <StatusBadge state="unknown" />
          </div>
        ) : (
          <DeviceNode siteId={siteId} device={television} liveState={liveStates[television?.id]} remotePaired={remotePairing[television?.id]} adbConnection={adbConnections[television?.id]} role="Television" onSelect={onDeviceSelect} onOpenAndroidPowerSettings={onOpenAndroidPowerSettings} onOpenDevicePairing={onOpenDevicePairing}
            actions={television && <>
              {tvHomeEnabled && <button type="button" className="topology-live__activate-source topology-live__watch" onClick={() => onActivateTelevision?.(screen)} disabled={activationBusy}>Watch</button>}
              {screen.nativeConnector !== 'lg_webos_remote' && <button type="button" className="topology-live__activate-source" onClick={() => onLaunchSuitestream?.(television.id)}>Launch Suitestream</button>}
              {onOpenDeviceRemote && <button type="button" className="topology-live__activate-source" aria-label={`Open TV Remote for ${television.name}`} onClick={() => onOpenDeviceRemote(television.id)}>TV Remote</button>}
            </>}
          />
        )}
        {!screenInactive && activePort ? (
          <p className="topology-live__reported-source topology-live__reported-source--confirmed">
            Selected input: HDMI {activePort} · {tvInputReport?.activeSource?.adapter || 'TV telemetry'}
          </p>
        ) : !screenInactive && television?.source && (
          <p className="topology-live__reported-source">Expected TV input: {television.source} · live state unavailable</p>
        )}
      </div>

      <div className={`topology-live__connections ${activeDeviceId ? 'topology-live__connections--active' : ''}`}>
        {players.length ? players.map((player, index) => {
          const deviceId = player.deviceId || player.id;
          const playerDevice = devicesById.get(Number(deviceId));
          const playerPort = hdmiPort(player.expectedInput);
          const playerPower = resolvePowerObservations(liveStates[playerDevice?.id], powerContext.byId[playerDevice?.id]);
          const playerDisplay = playerPower.fields.displayState?.value;
          const playerOff = playerDisplay === 'OFF' || (!playerDisplay && playerPower.fields.dozing?.value !== true
            && playerPower.fields.dreaming?.value !== true && getDeviceOperationalState(liveStates[playerDevice?.id]).power.state === 'asleep');
          const isActive = Number(deviceId) === activeDeviceId;
          const suggestion = suggestions.find((entry) => Number(entry.playerDeviceId) === Number(deviceId));
          return (
            <div className={`topology-live__connection ${isActive ? 'topology-live__connection--active' : activePort && !screenInactive ? 'topology-live__connection--inactive' : ''} ${playerOff ? 'topology-live__connection--powered-off' : ''}`} key={`${screen.id}-player-${deviceId || index}`}>
              <div className="topology-live__cable">
                <span className="topology-live__cable-line" />
                <span className="topology-live__port">{player.expectedInput || 'Port not set'}</span>
                <span className="topology-live__cable-line" />
              </div>
              <DeviceNode siteId={siteId}
                device={playerDevice}
                liveState={liveStates[playerDevice?.id]}
                remotePaired={remotePairing[playerDevice?.id]}
                adbConnection={adbConnections[playerDevice?.id]}
                role={!isVideoConnection(player, playerDevice) ? 'Audio / other equipment' : Number(player.deviceId || player.id) === Number(screen.preferredPlayerDeviceId) ? 'Preferred source' : 'Alternative source'}
                onSelect={onDeviceSelect} onOpenAndroidPowerSettings={onOpenAndroidPowerSettings} onOpenDevicePairing={onOpenDevicePairing}
                actions={playerDevice && isVideoConnection(player, playerDevice) && <>
                  {(playerPort || player.connectionConfig?.inputRoute?.nativeInputId) && hasSourceActivationRoute(screen, player, enabledPorts.has(playerPort), { avoidSourceHome: true }) && (
                    <button type="button" className="topology-live__activate-source topology-live__watch" onClick={() => onActivatePlayer?.(screen, player)} disabled={activationBusy}>Watch</button>
                  )}
                  <button type="button" className="topology-live__activate-source" onClick={() => onLaunchSuitestream?.(playerDevice.id)}>Launch Suitestream</button>
                  {onOpenDeviceRemote && <button type="button" className="topology-live__activate-source" aria-label={`Open TV Remote for ${playerDevice.name}`} onClick={() => onOpenDeviceRemote(playerDevice.id)}>TV Remote</button>}
                </>}
              />
              {suggestion && (
                <div className="topology-live__suggestion">
                  <span>Suggested: {suggestion.playerName} → {suggestion.expectedInput} · {suggestion.mappingConfidence} confidence</span>
                  <button type="button" onClick={() => onConfirmMapping?.(suggestion)}>Confirm mapping</button>
                </div>
              )}
            </div>
          );
        }) : (
          <div className="topology-live__empty-connection">No players connected</div>
        )}
      </div>
      <div className="topology-live__strategy-summary">
        Input switching: {switchingMethods.length ? switchingMethods.join(' → ').replaceAll('_', ' ') : 'not configured'}
      </div>
    </article>
  );
}

export function buildInheritedAudioRoutes(endpoint, screens, tvInputReports = {}) {
  const screen = (screens || []).find((entry) => Number(entry.id) === Number(endpoint.logicalScreenId));
  if (!screen) return { screen: null, routes: [], activePlayerDeviceId: null };
  const report = screen.inputReport || tvInputReports[Number(screen.televisionDeviceId)];
  const activePort = report?.activeSource?.available ? Number(report.activeSource.port) || null : null;
  const routes = (screen.players || []).map((player) => ({
    player,
    port: hdmiPort(player.expectedInput),
    active: Boolean(activePort && hdmiPort(player.expectedInput) === activePort),
  }));
  return {
    screen,
    routes,
    activePlayerDeviceId: routes.find((route) => route.active)?.player?.deviceId || null,
  };
}

function AudioCard({ siteId, endpoint, screens, devicesById, liveStates, remotePairing, adbConnections, tvInputReports, onDeviceSelect, onOpenAndroidPowerSettings, onOpenDevicePairing }) {
  const inherited = buildInheritedAudioRoutes(endpoint, screens, tvInputReports);
  const television = devicesById.get(Number(inherited.screen?.televisionDeviceId));
  const source = devicesById.get(Number(endpoint.sourceDeviceId)) || television;
  return (
    <article className="topology-live__audio">
      <header className="topology-live__card-header">
        <div><span className="topology-live__eyebrow">Audio Endpoint</span><h3>{endpoint.name}</h3></div>
        <span className="topology-live__kind">Audio</span>
      </header>
      <div className="topology-live__audio-route">
        <span className="topology-live__audio-wave">)))</span>
        <DeviceNode siteId={siteId} device={source} liveState={liveStates[source?.id]} remotePaired={remotePairing[source?.id]} adbConnection={adbConnections[source?.id]} role={inherited.screen ? 'TV default audio output' : 'Current source'} onSelect={onDeviceSelect} onOpenAndroidPowerSettings={onOpenAndroidPowerSettings} onOpenDevicePairing={onOpenDevicePairing} />
      </div>
      {inherited.screen && (
        <div className="topology-live__inherited-audio">
          <strong>Audio follows the selected TV input</strong>
          <p>{inherited.screen.name} uses its configured/default audio output. Selecting an attached player carries that player’s audio through the TV to {endpoint.name}; no player audio-output change is required.</p>
          <div className="topology-live__audio-chain-list">
            {inherited.routes.map(({ player, port, active }) => {
              const playerDevice = devicesById.get(Number(player.deviceId || player.id));
              return (
                <div className={active ? 'is-active' : ''} key={player.deviceId || player.id}>
                  <span>{playerDevice?.name || player.name || `Device ${player.deviceId || player.id}`}</span>
                  <b>→</b><span>{port ? `HDMI ${port}` : 'TV input'}</span>
                  <b>→</b><span>{television?.name || inherited.screen.name}</span>
                  <b>→</b><span>TV default output</span>
                  <b>→</b><span>{endpoint.name}</span>
                  {active && <em>effective now</em>}
                </div>
              );
            })}
          </div>
          <small>Automation rule: preserve this inherited route unless the requested scene explicitly changes the TV’s audio output.</small>
        </div>
      )}
    </article>
  );
}

function TopologyOverview({ siteId, topology, devices, liveStates = {}, remotePairing = {}, adbConnections = {}, nativeNodes = {}, tvInputReports = {}, discoveryStatus, sourceActivation, onDiscoverConnections, onOpenGuidedSourceSetup, onActivatePlayer, onActivateTelevision, onLaunchSuitestream, onOpenDeviceRemote, onOpenAndroidPowerSettings, onOpenDevicePairing, onConfirmMapping, onDeviceSelect, onOpenSetup, refreshedAt, showStatus = true, showConnections = true, screenshotsEnabled = false, screenshotScope = '', previewsReady = false }) {
  const [, setClock] = useState(0);
  // Expire evidence even when every socket is silent.
  useEffect(() => {
    const timer = window.setInterval(() => setClock((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const devicesById = useMemo(() => new Map(devices.map((device) => [Number(device.id), device])), [devices]);
  const screens = topology?.screens || [];
  const powerContext = useContext(AreaPowerContext);
  const entries = screens.map((screen) => {
    const nowPlaying = getScreenNowPlaying(screen, devicesById, liveStates, tvInputReports[Number(screen.televisionDeviceId)],
      Date.now(), powerContext.enabled ? powerContext.byId : {});
    const target = resolveScreenshotTarget(nowPlaying, devicesById, liveStates, powerContext.byId, nativeNodes);
    return { screenId: screen.id, nowPlaying, target, wantsSnapshot: canShowActivity(nowPlaying) && !showsSuitestream(nowPlaying) };
  });
  const previews = useScreenPreviews({ entries, scopeKey: screenshotScope, enabled: screenshotsEnabled && showConnections,
    ready: previewsReady && !['sending', 'verifying'].includes(sourceActivation?.status) && discoveryStatus?.status !== 'loading' });
  const audioEndpoints = topology?.audioEndpoints || [];
  const configuredIds = new Set(screens.flatMap((screen) => [
    screen.televisionDeviceId,
    ...(screen.players || []).map((player) => player.deviceId || player.id),
  ]).concat(audioEndpoints.map((endpoint) => endpoint.sourceDeviceId)).filter(Boolean).map(Number));
  const unassigned = devices.filter((device) => !configuredIds.has(Number(device.id)));
  const configured = screens.length > 0 || audioEndpoints.length > 0;
  const suggestions = useMemo(
    () => buildHdmiMappingSuggestions(topology, devices, tvInputReports),
    [devices, topology, tvInputReports]
  );
  const wirelessWarnings = devices.filter((device) => wirelessDebuggingNeedsAttention(adbConnections[device.id]));
  const accessibilityWarnings = devices.filter((device) => (
    getDeviceAccessibilityState(device, liveStates[device.id]) === 'disabled'
  ));
  const systemOverlayWarnings = devices.filter((device) => (
    getDeviceSystemOverlayState(device, liveStates[device.id]) === 'disabled'
  ));

  return (
    <div className="topology-live">
      {showStatus && <div className="topology-live__summary">
        <div>
          <span className="topology-live__pulse" /> Live area status
          <small>{refreshedAt ? `Inventory refreshed ${refreshedAt.toLocaleTimeString()}` : 'Loading current status…'}</small>
        </div>
        <div className="topology-live__summary-actions">
          {configured && <button type="button" onClick={onDiscoverConnections} disabled={discoveryStatus?.status === 'loading'}>{discoveryStatus?.status === 'loading' ? 'Discovering…' : 'Discover connections'}</button>}
          {configured && <button type="button" onClick={onOpenGuidedSourceSetup}>Guided Source Setup</button>}
          <button type="button" onClick={onOpenSetup}>{configured ? 'Edit connections' : 'Set up Screens'}</button>
        </div>
      </div>}

      {showStatus && <p className="topology-live__discovery">
        Player, background service and ADB reconnect independently. Connection loss can take several seconds to detect during a restart;
        the ADB check age shows when that connection was last verified.
      </p>}

      {showStatus && discoveryStatus?.message && <p className={`topology-live__discovery topology-live__discovery--${discoveryStatus.status}`}>{discoveryStatus.message}</p>}
      {(showStatus || showConnections) && sourceActivation?.message && <p className={`topology-live__activation topology-live__activation--${sourceActivation.status}`}>{sourceActivation.message}</p>}
      {showStatus && wirelessWarnings.length > 0 && (
        <div className="topology-live__accessibility-alert" role="alert">
          <strong>Wireless Debugging needs attention</strong>
          <span>{wirelessWarnings.map((device) => device.name || `Device ${device.id}`).join(', ')}</span>
          <small>These paired ADB connections could not be verified after automatic retries. Review each device’s local recovery status below,
            then check power, network and any Wi-Fi approval prompt. Suitestream will retry automatically.</small>
        </div>
      )}
      {showStatus && accessibilityWarnings.length > 0 && (
        <div className="topology-live__accessibility-alert" role="alert">
          <strong>Accessibility needs attention</strong>
          <span>{accessibilityWarnings.map((device) => device.name || `Device ${device.id}`).join(', ')}</span>
          <small>Suitestream has explicitly reported Accessibility as disabled. Re-enable it in Android settings on each listed device.</small>
        </div>
      )}
      {showStatus && systemOverlayWarnings.length > 0 && (
        <div className="topology-live__accessibility-alert" role="alert">
          <strong>Display over other apps needs attention</strong>
          <span>{systemOverlayWarnings.map((device) => device.name || `Device ${device.id}`).join(', ')}</span>
          <small>Suitestream has explicitly reported Display over other apps as disabled. Re-enable it in Android settings on each listed device.</small>
        </div>
      )}

      {showConnections && (configured ? (
        <>
          <div className="topology-live__screen-grid">
            {screens.map((screen, index) => <ScreenCard siteId={siteId} key={screen.id} screen={screen} devicesById={devicesById} liveStates={liveStates} remotePairing={remotePairing} adbConnections={adbConnections} tvInputReport={screen.inputReport || tvInputReports[Number(screen.televisionDeviceId)]} suggestions={suggestions.filter((entry) => entry.screenId === screen.id)} sourceActivation={sourceActivation} onActivatePlayer={onActivatePlayer} onActivateTelevision={onActivateTelevision} onLaunchSuitestream={onLaunchSuitestream} onOpenDeviceRemote={onOpenDeviceRemote} onOpenAndroidPowerSettings={onOpenAndroidPowerSettings} onOpenDevicePairing={onOpenDevicePairing} onConfirmMapping={onConfirmMapping} onDeviceSelect={onDeviceSelect} entry={entries[index]} preview={previews[screen.id]} />)}
          </div>
          {audioEndpoints.length > 0 && <div className="topology-live__audio-grid">{audioEndpoints.map((endpoint) => <AudioCard siteId={siteId} key={endpoint.id} endpoint={endpoint} screens={screens} devicesById={devicesById} liveStates={liveStates} remotePairing={remotePairing} adbConnections={adbConnections} tvInputReports={tvInputReports} onDeviceSelect={onDeviceSelect} onOpenAndroidPowerSettings={onOpenAndroidPowerSettings} onOpenDevicePairing={onOpenDevicePairing} />)}</div>}
        </>
      ) : (
        <div className="topology-live__unconfigured">
          <h3>No Screen connections configured yet</h3>
          <p>Device status is still live. Open Setup when you are ready to group televisions, players and audio.</p>
        </div>
      ))}

      {showConnections && (!configured || unassigned.length > 0) && (
        <section className="topology-live__other">
          <h3>{configured ? 'Unassigned devices' : 'Devices in this area'}</h3>
          <div className="topology-live__device-grid">
            {(configured ? unassigned : devices).map((device) => (
              <DeviceNode siteId={siteId} key={device.id} device={device} liveState={liveStates[device.id]} remotePaired={remotePairing[device.id]} adbConnection={adbConnections[device.id]} role={isRemoteDevice(device) ? 'Remote' : 'Physical device'} onSelect={onDeviceSelect} onOpenAndroidPowerSettings={onOpenAndroidPowerSettings} onOpenDevicePairing={onOpenDevicePairing} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default TopologyOverview;
